<template>
  <template v-for="(link, idx) in menuItems" :key="idx">
    <router-link
      v-if="isRouteItem(link)"
      :to="link.to"
      class="menu-items-with-bird__link heading--h3"
      @click="$emit('navigate')"
    >
      <USIcon
        :diameter="15"
        name="upswing-bird"
        class="menu-items-with-bird__upswing-bird"
        color="var(--icon-color-upswing-bird)"
      />
      <span class="menu-items-with-bird__label">
        {{ link.label }}
      </span>
    </router-link>
    <button
      v-else
      type="button"
      class="menu-items-with-bird__link menu-items-with-bird__link--action menu-items-with-bird__link--nav-action heading--h3"
      @click="onActionClick(link)"
    >
      <span class="menu-items-with-bird__nav-action-label">
        <USIcon
          v-if="link.icon"
          :name="link.icon"
          :diameter="18"
          class="menu-items-with-bird__nav-action-icon"
          color="var(--color--primary-b1)"
        />
        <span class="menu-items-with-bird__label">
          {{ link.label }}
        </span>
      </span>
    </button>
  </template>
</template>
<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { RouteLocationRaw } from 'vue-router'

import USIcon from '@base/components/USIcon/USIcon.vue'
import type { PlayerPrimaryNavLink } from '@base/_types/PlayerPrimaryNavLink'

export type MenuItemWithBird = PlayerPrimaryNavLink

function isRouteItem(link: PlayerPrimaryNavLink): link is {
  label: string
  to: RouteLocationRaw
} {
  return 'to' in link
}

export default defineComponent({
  name: 'MenuItemsWithBird',
  components: { USIcon },
  props: {
    menuItems: {
      type: Array as PropType<PlayerPrimaryNavLink[]>,
      default: () => []
    }
  },
  emits: ['navigate', 'add-event'],
  setup(_, { emit }) {
    function onActionClick(link: PlayerPrimaryNavLink) {
      if ('action' in link && link.action === 'add-event') {
        emit('add-event')
      }
      emit('navigate')
    }
    return { isRouteItem, onActionClick }
  }
})
</script>

<style lang="scss" scoped>
.menu-items-with-bird {
  &__link {
    align-items: center;
    background: none;
    border: none;
    border-radius: var(--border-radius--small);
    color: var(--color--primary-b2);
    cursor: pointer;
    display: flex;
    flex-flow: row;
    font: inherit;
    padding: 0;
    text-align: inherit;
    transition: var(--animation-speed-medium); // TODO add motion
    width: fit-content;

    &:not(:last-child) {
      margin-bottom: var(--spacing--3);
    }

    &:hover {
      .menu-items-with-bird__upswing-bird {
        display: block;
        opacity: 1;
      }
    }

    &-icon {
      margin-right: var(--spacing--6);
    }
  }

  &__link--nav-action {
    background-color: var(--color--secondary-c2);
    border-radius: 6px;
    color: var(--color--primary-b1);
    /* Spacing scale inverted: --spacing--8≈6px, --spacing--7≈14px (Cursor rule design-tokens) */
    padding: var(--spacing--8) var(--spacing--7);
    transition: background-color 0.15s ease;

    &:hover {
      /* Match PlayerHeader nav-action hover — subtle darken of #D4EFFC */
      background-color: #c8e5f6;
    }
  }

  &__nav-action-label {
    align-items: center;
    display: inline-flex;
    flex-flow: row nowrap;
    gap: 0.4rem;
  }

  &__nav-action-icon {
    flex-shrink: 0;
  }

  &__upswing-bird {
    --icon-color-upswing-bird: var(--color--secondary-c1);
    display: none;
    margin-right: var(--spacing--7);
    opacity: 0;
  }
}
</style>
